import React, { useCallback, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cloneDeep, flatten, get, sumBy, map } from 'lodash-es';
import moment from 'moment-timezone';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Button,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Table,
} from 'reactstrap';
import {
  faInfo,
  faEnvelope,
  faPenSquare,
  faPrint,
  faStickyNote,
  faCreditCard,
  faExclamationTriangle,
  faCheckCircle,
  faDownload,
  faDollarSign,
  faCircleXmark
} from '@fortawesome/free-solid-svg-icons';
import ReactTable from 'react-table';
import axios from 'axios';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { preformatFloat, exportCSV, locale, formatCurrencyForLocale } from '../../../utilities/invoiceUtils';
import { imagePath } from '../../../utilities/UrlPaths';
import { useAxios } from '../../../hooks';
import Page from '../../Page';
import InvoiceGroupTableCols from './InvoiceGroupTableCols';
import AddNoteModal from '../AddNoteModal';
import ContactModal from '../ContactModal';
import ChargeCardModal from './ChargeCardModal';
import CalendarModal from '../CalendarModal';
import { formatChannelText } from '../../../utilities/textsFormatters';
import PrintableInvoiceModal from './PrintableInvoiceModal';
import InvaluableShippingBanner from '../InvaluableShippingBanner/InvaluableShippingBanner';
import { selectIsInvShippingEnabled } from '../../../selectors/policySelectors';
import { shippingApi } from '../../../api/shipping';

const blockEl = 'invoices__invoice-group-details';

const InvoiceGroupDetails = () => {
  const [invoices, setInvoices] = useState([]);
  const [tableInvoiceData, setTableInvoiceData] = useState([]);
  const [shippingData, setShippingData] = useState({});
  const [isShippingDataLoading, setIsShippingDataLoading] = useState(false);
  const [preFilteredArray, setPreFilteredArray] = useState([]);
  const [printableInvoiceData, setPrintableInvoiceData] = useState([]);
  const [printableInvoiceIds, setPrintableInvoiceIds] = useState([]);
  const [checkedInvoices, setCheckedInvoices] = useState({});
  const [chargeableInvoiceData, setChargeableInvoiceData] = useState({});
  const [hasPaddles, setHasPaddles] = useState(false);
  const [isBulkAddNoteModalOpen, setIsBulkAddNoteModalOpen] = useState(false);
  const [isBulkContactModalOpen, setIsBulkContactModalOpen] = useState(false);
  const [currentInvoiceID, setCurrentInvoiceID] = useState(0);
  const [currentInvoiceNotes, setCurrentInvoiceNotes] = useState('');
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [modalNameObject, setModalNameObject] = useState({});
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchError, setShowSearchError] = useState(false);
  const [showChargeCardLink, setShowChargeCardLink] = useState(false);
  const [isChargeCardModalOpen, setIsChargeCardModalOpen] = useState(false);
  const [showPaymentSuccessBanner, setShowPaymentSuccessBanner] = useState(false);
  const [showPaymentErrorBanner, setShowPaymentErrorBanner] = useState(false);
  const [showPaymentErrorGeneric, setShowPaymentErrorGeneric] = useState(false);
  const [paymentSuccessCount, setPaymentSuccessCount] = useState(0);
  const [paymentErrorCount, setPaymentErrorCount] = useState(0);
  const [paymentTotalCount, setPaymentTotalCount] = useState(0);
  const [paymentErrorIdArray, setPaymentErrorIdArray] = useState([]);
  const [isCardProcessing, setIsCardProcessing] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [calendarID, setCalendarID] = useState(0);
  const [calendarDetailID, setCalendarDetailID] = useState(0);
  const [calendarVariant, setCalendarVariant] = useState('');
  const [calendarPickupFlag, setCalendarPickupFlag] = useState(false);
  const [calendarPaymentMethod, setCalendarPaymentMethod] = useState(0);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarCurrentStatus, setCalendarCurrentStatus] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showRestrictedItemText, setShowRestrictedItemText] = useState(false);
  const [isSingleRow, setIsSingleRow] = useState(false);
  const [disablePreviewButton, setDisablePreviewButton] = useState(true);
  const [eligibleInvoicesToProcess, setEligibleInvoicesToProcess] = useState([]);
  const [eligibleStatus, setEligibleStatus] = useState([]);
  const [filterView, setFilterView] = useState('all');
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [searchFormCleared, setSearchFormCleared] = useState(false);
  const [viewFilterChange, setViewFilterChange] = useState(false);
  const [moreMenuId, setMoreMenuId] = useState(null);
  const moreMenuRef = useRef({});

  // Handle clicks outside the more menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuId && moreMenuRef.current[moreMenuId]) {
        const menuElement = moreMenuRef.current[moreMenuId];
        if (!menuElement.contains(event.target)) {
          setMoreMenuId(null);
        }
      }
    };
    if (moreMenuId) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [moreMenuId]);

  // handle total invoice and pending charges
  const additionalCharges = { addCharges: [{ charges: {
    additionalCharges: 0
  } }] };

  const pendingAdditionalCharges = { addCharges: [{ charges: {
    additionalCharges: 0
  } }] };

  const [currentTotalInvoice, setCurrentTotalInvoice] = useState(0);
  const [currentPendingInvoice, setCurrentPendingInvoice] = useState(0);
  const [prevAdditionalCharges, setAdditionalCharges] = useState(additionalCharges);
  const [prevPendingAdditionalCharges, setPendingAdditionalCharges] = useState(pendingAdditionalCharges);
  const [totalItemCount, setTotalItemCount] = useState(0);
  const [canProcessCards, setCanProcessCards] = useState(false);
  const [showAchBanner, setShowAchBanner] = useState(false);
  const [groupInfoData, setGroupInfoData] = useState([]);
  const [rawPrintData, setRawPrintData] = useState([]);
  const [allInvoicesIds, setAllInvoicesIds] = useState([]);
  const { invoiceGroupID } = useParams();
  const { houseRef, houseID, paymentProcessingEnabled, houseAddress, autoChargeEnabled } = useSelector(state => state.house);
  const { processorType } = useSelector(state => state.invoice);

  const user = useSelector(state => state.user);
  const isInvShippingEnabled = useSelector(selectIsInvShippingEnabled);
  const printRef = useRef();

  const { data, isIdle, isLoading, error } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef]
  );

  const { exec: execNotes, isError: isNotesError } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const { exec: execMessage } = useAxios(
    () => `/app/house/${houseRef}/invoiceMessage`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { data: paymentMethodsData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/payment-methods`,
    [houseRef]
  );
  const { exec: execPaidDate } = useAxios(
    () => `/app/house/${houseRef}/invoicePaidOnly`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execShippedDate } = useAxios(
    () => `/app/house/${houseRef}/invoiceShippedOnly`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const { exec: chargeCards, data: chargeCardsData, isError: chargeCardsError } = useAxios(
    () => `/app/house/${houseRef}/processCards`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { data: canProcessCardsData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupInfo/processCards`,
    [houseRef]
  );

  const formatMomentDateToUTC = (date) => moment(moment(date, 'MM/DD/YYYY h:mm A zz').utc().format('MM/DD/YYYY'));

  useEffect(() => {
    if (!data) return;
    setInvoices(get(data, 'data.data.invoiceBidders', []));
    setFilteredData(get(data, 'data.data.invoiceBidders', []));
    setHasPaddles(get(data, 'data.data.hasPaddleNumbers', false));
    setCatalogs(get(data, 'data.data.catalogs', []));
  }, [data]);

  useEffect(() => {
    if (typeof canProcessCardsData === 'undefined') {
      return;
    }
    const processDataArray = get(canProcessCardsData, 'data.data.invoiceGroupInfo.invoices', []);
    setGroupInfoData(processDataArray);
    const processFlag = processDataArray.some(item => item.canHouseProcessCards === true);

    setCanProcessCards(processFlag);
  }, [canProcessCardsData]);

  const canProcessInvoice = (id) => {
    if (groupInfoData.length > 0) {
      const currentInvoice = groupInfoData.find(
        (invoice) => invoice.invoiceID === id
      );
      if (typeof currentInvoice !== 'undefined') {
        return currentInvoice.canHouseProcessCards;
      }
    }

    return false;
  };

  useEffect(() => {
    const invoicesData = filterView !== 'all' ? filteredData : invoices;
    if (invoices.length > 0) {
      setCurrencySymbol(invoices[0].totalPrice.currencySymbol);

      let grandTotal = 0;
      let pendingTotal = 0;
      invoicesData.forEach((invoice) => {
        grandTotal += parseFloat(preformatFloat(invoice.invoiceTotal));
        pendingTotal += parseFloat(preformatFloat(invoice.outStandingBalance));
      });

      setCurrentTotalInvoice(grandTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      setCurrentPendingInvoice(pendingTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
  }, [invoices, filteredData]);

  const invoicesWithACHPaymentRequiredCount = useMemo(() => sumBy(invoices, 'creditCardLimitExceeded'), [invoices]);

  useEffect(() => {
    if (invoicesWithACHPaymentRequiredCount) {
      setShowAchBanner(true);
    }
  }, [invoicesWithACHPaymentRequiredCount]);

  useEffect(() => {
    if (catalogs.length === 0) {
      return;
    }

    const isPaymentContact = get(user, 'paymentUser', false);
    const allInvoicesAreACH = invoicesWithACHPaymentRequiredCount === invoices.length;

    //  check for unpaid
    let hasUnpaidInvoices = false;
    invoices.forEach((invoice) => {
      if (!invoice.allPaid) {
        hasUnpaidInvoices = true;
      } else {
        //  check for additional charges by invoice
        if (invoice.additionalPriceInfo !== undefined && invoice.additionalPriceInfo !== null && !invoice.additionalPriceInfo.paid) {
          hasUnpaidInvoices = true;
        }
        //  check for additional charges by lot
        invoice.invoiceDetails.forEach((detail) => {
          if (detail.additionalCharge && detail.additionalPriceInfo && !detail.additionalPriceInfo.paid) {
            hasUnpaidInvoices = true;
          }
        });
      }
    });

    setShowChargeCardLink(isPaymentContact && canProcessCards && paymentProcessingEnabled && hasUnpaidInvoices && !allInvoicesAreACH);
  }, [catalogs, canProcessCards]);

  useEffect(() => {
    if (typeof paymentMethodsData === 'undefined') {
      return;
    }

    const methods = get(paymentMethodsData, 'data.data.paymentMethods', []);

    setPaymentMethods(methods);
  }, [paymentMethodsData]);

  const sessionsInGroup = useMemo(
    () => get(data, 'data.data.catalogs', []),
    [data]
  );

  const allInvoicesDetails = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    return flatten(map(invoices, 'invoiceDetails'));
  }, [invoices]);

  useEffect(() => {
    if (!allInvoicesDetails) return;

    let detailsData = allInvoicesDetails;
    if (filterView !== 'all') {
      detailsData = flatten(map(filteredData, 'invoiceDetails'));
    }
    const unsuppressedItems = detailsData.filter(item => item.suppress === false);
    setTotalItemCount(unsuppressedItems.length);
  }, [allInvoicesDetails, filteredData]);

  useEffect(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    const invoiceMap = map(invoices, 'invoiceID');
    setAllInvoicesIds(invoiceMap);
  }, [invoices]);

  useEffect(() => {
    if (typeof allInvoicesIds === 'undefined' || !allInvoicesIds.length) return;

    const getPrintData = async () => {
      const printData = await axios.post(`/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/invoicePreview`, allInvoicesIds);
      setRawPrintData(get(printData, 'data.data.invoiceGroups', []));
    };
    getPrintData();
  }, [allInvoicesIds]);

  const isEveryInvoicesChecked = useMemo(() => {
    if (!allInvoicesIds.length
      || !Object.keys(checkedInvoices).length
      || Object.keys(checkedInvoices).length !== allInvoicesIds.length) {
      return false;
    }

    return Object.keys(checkedInvoices).every((invoiceID) => checkedInvoices[invoiceID]);
  }, [checkedInvoices, allInvoicesIds]);

  const sortedSessionsInGroup = useMemo(() => {
    if (typeof sessionsInGroup === 'undefined' || !sessionsInGroup.length) {
      return [];
    }

    return cloneDeep(sessionsInGroup)
      .sort((a, b) => a.identifier.normalize().localeCompare(b.identifier.normalize()));
  }, [sessionsInGroup]);

  const firstSessionGroupDate = useMemo(() => {
    if (typeof sessionsInGroup === 'undefined' || !sessionsInGroup.length) {
      return;
    }

    const [{ date }] = sessionsInGroup.length === 1
      ? sessionsInGroup
      : cloneDeep(sessionsInGroup).sort((a, b) => formatMomentDateToUTC(a.date).diff(
        formatMomentDateToUTC(b.date, 'days')
      ));

    return formatMomentDateToUTC(date);
  }, [sessionsInGroup]);

  const getDiffDateNumber = useCallback(
    (catalogDate) => {
      if (
        typeof firstSessionGroupDate === 'undefined'
        || !firstSessionGroupDate.isValid()
      ) {
        return null;
      }

      return (
        formatMomentDateToUTC(catalogDate).diff(firstSessionGroupDate, 'days')
        + 1
      );
    },
    [firstSessionGroupDate]
  );

  const formatDateString = (dateString) => {
    const date = new Date(dateString);
    const mPart = (date.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
    const dPart = date.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
    const yPart = date.getFullYear().toString().substring(2);
    return `${mPart}/${dPart}/${yPart}`;
  };

  const formatDateMillis = (date) => moment.tz(date, houseAddress.timeZoneID).format('MM/DD/YY');
  const formatDateTimestamp = (date) => moment.tz(date, houseAddress.timeZoneID).format('h:mm a zz');
  const hasUnpaidAdditionalCharges = (invoice) => {
    let returnFlag = false;
    const hasInvoiceLevelCharges = (invoice.additionalPriceInfo !== undefined && invoice.additionalPriceInfo !== null && !invoice.additionalPriceInfo.paid);
    let hasLotLevelCharges = false;
    invoice.invoiceDetails.forEach(lot => {
      if (lot.additionalPriceInfo !== undefined && lot.additionalPriceInfo !== null && !lot.additionalPriceInfo.paid) {
        hasLotLevelCharges = true;
      }
    });
    if (invoice.allPaid && (hasInvoiceLevelCharges || hasLotLevelCharges)) {
      returnFlag = true;
    }

    return returnFlag;
  };

  const getInvoiceDetailStatus = (id, type, object = false) => {
    const foundInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    const invoiceSentDateString = get(foundInvoice, 'invoiceSentDateString', '');
    const invoiceSentTimestamp = get(foundInvoice, 'invoiceSentDate.time', 0);
    const paymentStat = get(foundInvoice, 'onlinePaymentStat', '');
    const isFailedPayment = paymentStat === 'd' || paymentStat === 'f';
    const isPendingPayment = paymentStat === 'p' || paymentStat === 'u' || paymentStat === 'h' || paymentStat === 'i';

    const shipped = (item) => get(item, 'shipped', false);
    const allShipped = foundInvoice.invoiceDetails.every(shipped);
    const partialShipped = foundInvoice.invoiceDetails.some(shipped);
    const shipDateArray = foundInvoice.invoiceDetails.map((item) => ((item.shippedDateString) ? new Date(item.shippedDateString) : ''));
    const maxShippedDate = new Date(Math.max.apply(null, shipDateArray));
    const shippedText = foundInvoice.pickup ? 'Picked Up' : 'Shipped';

    const paid = (item) => get(item, 'paidDateString', '') !== '';
    const itemsArraySuppress = foundInvoice.invoiceDetails.filter(item => !item.suppress);
    const checkPaid = (item) => item.paid;
    const nonSuppressedPaid = itemsArraySuppress.length > 0 && itemsArraySuppress.every(checkPaid);
    const allPaid = foundInvoice.allPaid || nonSuppressedPaid;
    const partialPaid = (foundInvoice.invoiceDetails.some(paid) || hasUnpaidAdditionalCharges(foundInvoice));
    const paidDateArray = foundInvoice.invoiceDetails.map((item) => ((item.paidDateString) ? new Date(item.paidDateString) : ''));
    const maxPaidDate = Math.max(...paidDateArray);

    const paidTimestampArray = foundInvoice.invoiceDetails.map((item) => ((item.paidDate) ? item.paidDate.time : 0));
    const maxPaidTimestamp = Math.max(...paidTimestampArray);

    //  object for CSV formatting
    if (object) {
      let returnArray = [];
      if (type === 'paid') {
        if (allPaid && !hasUnpaidAdditionalCharges(foundInvoice)) {
          returnArray = ['Paid', `${formatDateString(maxPaidDate)} ${formatDateTimestamp(maxPaidTimestamp)}`];
        } else if (isPendingPayment) {
          returnArray = ['Payment Pending', ''];
        } else if (partialPaid) {
          returnArray = ['Partial Paid', `${formatDateString(maxPaidDate)} ${formatDateTimestamp(maxPaidTimestamp)}`];
        } else if (isFailedPayment) {
          returnArray = ['Payment Failed', maxPaidDate ? `${formatDateString(maxPaidDate)} ${formatDateTimestamp(maxPaidTimestamp)}` : ''];
        } else if (invoiceSentDateString) {
          returnArray = ['Sent', `${formatDateString(invoiceSentDateString)} ${formatDateTimestamp(invoiceSentTimestamp)}`];
        } else {
          returnArray = ['Not Sent', ''];
        }
      } else if (type === 'ship') {
        if (allShipped) {
          returnArray = [shippedText, formatDateString(maxShippedDate)];
        } else if (partialShipped) {
          returnArray = [`Partial ${shippedText}`, formatDateString(maxShippedDate)];
        } else {
          returnArray = [`Not ${shippedText}`, ''];
        }
      }
      return returnArray;
    }
    //  website status
    if (type === 'paid') {
      const returnObject = {};
      if (allPaid && !hasUnpaidAdditionalCharges(foundInvoice)) {
        returnObject.status = `Paid - ${formatDateMillis(maxPaidTimestamp)}`;
        returnObject.timestamp = formatDateTimestamp(maxPaidTimestamp);
      } else if (isPendingPayment) {
        returnObject.status = 'Payment Pending';
        returnObject.timestamp = '';
      } else if (partialPaid) {
        returnObject.status = `Partial Paid - ${formatDateMillis(maxPaidTimestamp)}`;
        returnObject.timestamp = formatDateTimestamp(maxPaidTimestamp);
      } else if (isFailedPayment) {
        returnObject.status = `Payment Failed ${maxPaidTimestamp ? '-' : ''} ${maxPaidTimestamp ? formatDateMillis(maxPaidTimestamp) : ''}`;
        returnObject.timestamp = maxPaidTimestamp ? formatDateTimestamp(maxPaidTimestamp) : '';
      } else if (invoiceSentDateString) {
        returnObject.status = `Sent - ${formatDateMillis(invoiceSentTimestamp)}`;
        returnObject.timestamp = formatDateTimestamp(invoiceSentTimestamp);
      } else {
        returnObject.status = 'Not Sent';
        returnObject.timestamp = '';
      }
      return returnObject;
    }
    if (type === 'ship') {
      let returnString = '';
      if (allShipped) {
        returnString = `${shippedText} - ${formatDateString(maxShippedDate)}`;
      } else if (partialShipped) {
        returnString = `Partial ${shippedText} - ${formatDateString(maxShippedDate)}`;
      } else {
        returnString = `Not ${shippedText}`;
      }
      return returnString;
    }
  };

  const handleOnEditInvoices = () => {};

  const handleOnChangeInvoiceChecked = (invoiceId) => () => {
    setCheckedInvoices((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  const handleOnAllChangeInvoicesChecked = (e) => {
    if (!allInvoicesIds.length) {
      return;
    }

    const allCheckBehavior = {};
    allInvoicesIds.forEach((invoice) => {
      if (tableInvoiceData.length < allInvoicesIds.length) {
        allCheckBehavior[invoice] = false;
        for (let i = 0; i < tableInvoiceData.length; i++) {
          if (tableInvoiceData[i].invoiceID === invoice && filteredData.find((inv) => inv.invoiceID === invoice) !== undefined) {
            allCheckBehavior[invoice] = e.currentTarget.checked;
            break;
          }
        }
      } else {
        allCheckBehavior[invoice] = e.currentTarget.checked;
      }
    });

    setCheckedInvoices(allCheckBehavior);
  };

  const handleClickInvoiceLink = () => {
    window.sessionStorage.setItem('fromCreateInvoices', 'false');
  };

  const getSelectedIdArray = () => {
    const selectedIdArray = [];
    allInvoicesIds.map((id) => {
      if (checkedInvoices[id] && filteredData.find((invoice) => invoice.invoiceID === id) !== undefined) {
        selectedIdArray.push(id);
      }
    });
    return selectedIdArray;
  };

  useEffect(() => {
    const idArray = getSelectedIdArray();
    setDisablePreviewButton(idArray.length === 0);
  }, [checkedInvoices]);

  const handlePreviewButton = () => {
    const idArray = getSelectedIdArray();
    const idList = idArray.join(',');
    window.location = `${imagePath}/invoices/InvoiceGroupPreview/${invoiceGroupID}/invoice/${idList}`;
  };
  //  handle group or individual link to invoice
  if (processorType === 'p' || processorType === 's') {
    window.sessionStorage.setItem('autoChargeSource', 'group');
  } else {
    window.sessionStorage.removeItem('autoChargeSource');
  }

  const handleSingleContact = (payload, id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    if (
      typeof houseRef === 'undefined'
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.memberID
      || typeof payload !== 'object'
      || !Object.keys(payload).length
    ) {
      return;
    }

    execMessage({
      data: {
        ...payload,
        houseUserRef: user.userRef,
        bidderID: currentInvoice.memberID
      },
    });
  };

  const handleOnBulkContact = (payload) => {
    const selectedIds = getSelectedIdArray();
    selectedIds.forEach((id) => {
      handleSingleContact(payload, id);
    });
    setIsBulkContactModalOpen(false);
  };

  const handleSingleNote = (notes, id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);

    if (
      !notes
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.invoiceID
    ) {
      return;
    }

    execNotes({
      data: {
        invoiceNotes: [
          {
            notes,
            invoiceID: currentInvoice.invoiceID,
          },
        ],
      },
    });

    //color icon on groupDetails
    setTimeout(() => {
      const icon = document.getElementById(`noteIcon_${id}`);
      icon.classList.add('has-note');
    }, 500);

    return isNotesError;
  };

  const handleOnBulkAddNote = (notes) => {
    const selectedIds = getSelectedIdArray();
    selectedIds.forEach((id) => {
      handleSingleNote(notes, id);
    });

    setIsBulkAddNoteModalOpen(false);
  };

  const handleInlineNote = async () => {
    const notesValue = document.getElementById('newNote').value;
    const notesError = await handleSingleNote(notesValue, currentInvoiceID);
    //  it's already sent to the DB.  now rewrite back to local invoices object
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === currentInvoiceID);
    if (currentInvoice && !notesError) {
      currentInvoice.notes = notesValue;
    }
    setIsAddNoteModalOpen(false);
  };

  const handleInlineContact = async (payload) => {
    await handleSingleContact(payload, currentInvoiceID);
    setIsContactModalOpen(false);
  };

  const getRestrictedStatus = (id) => {
    let returnValue = false;
    const currentInvoice = filteredData.find((invoice) => invoice.invoiceID === id);
    currentInvoice.invoiceDetails.forEach((item) => {
      if (item.restricted) {
        returnValue = true;
        setShowRestrictedItemText(true);
      }
    });

    return returnValue;
  };

  // gets data for printable
  useEffect(() => {
    const getPrintableInvoices = async () => {
      const selectedIdArray = getSelectedIdArray();
      let printIdArray = allInvoicesIds;
      if (selectedIdArray.length) {
        printIdArray = selectedIdArray;
      } else if (filterView !== 'all') {
        const filteredIDs = filteredData.map(invoice => invoice.invoiceID);
        printIdArray = filteredIDs;
      }

      setPrintableInvoiceIds(printIdArray);
      try {
        if (printIdArray.length) {
          //  resorting invoice data as it's in a different order that groupDetails
          const printableData = [];
          printIdArray.forEach((id) => {
            const currentInvoice = rawPrintData.find(
              (item) => item.invoices.invoiceList[0].invoiceID === id.toString()
            );
            printableData.push(currentInvoice);
          });
          setPrintableInvoiceData(printableData);
        } else {
          setPrintableInvoiceData([]);
        }
      } catch (err) {
        setPrintableInvoiceData([]);
      }
    };

    getPrintableInvoices();
  }, [invoices, checkedInvoices, filteredData, rawPrintData]);

  const getPaymentMethodText = (id) => {
    let returnString = '';
    if (Number(id) > 0) {
      const filtered = paymentMethods.filter(item => item.id === id);
      if (filtered.length > 0) {
        returnString = filtered[0].name;
      }
    }

    return returnString;
  };

  const nonRestrictedPaidStatus = (id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    if (currentInvoice.allPaid) {
      return true;
    }
    const itemsArrayNR = currentInvoice.invoiceDetails.filter(item => !item.restricted && !item.suppress);
    const checkPaid = (item) => item.paid;
    return (itemsArrayNR.length > 0 && itemsArrayNR.every(checkPaid));
  };

  const nonRestrictedPaidByPP = (id) => {
    let returnFlag = false;
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    const itemsArrayNR = currentInvoice.invoiceDetails.filter(item => !item.restricted && !item.suppress);
    const checkPaidByPP = (item) => (item.paymentMethod.trim() === 'ACH') || (item.paymentMethod.trim() === 'paidByWePay');
    if (itemsArrayNR.length > 0) {
      returnFlag = itemsArrayNR.every(checkPaidByPP);
    }

    return returnFlag;
  };

  const isPendingPayment = (id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    const paymentStat = currentInvoice.onlinePaymentStat;
    return (paymentStat === 'p' || paymentStat === 'u' || paymentStat === 'h' || paymentStat === 'i');
  };

  const getEligibleStatus = (id, statusFlag = 0) => {
    const filtered = eligibleStatus.filter((row) => row.id === id);
    let returnVal;
    if (filtered.length === 0) {
      if (statusFlag === 1) {
        returnVal = 0;
      } else {
        returnVal = 'Not eligible';
      }
      return returnVal;
    }

    const currentRow = filtered[0];
    if (statusFlag === 1) {
      returnVal = currentRow.eligible;
    } else {
      returnVal = currentRow.failReason;
    }
    return returnVal;
  };

  const fetchShippingStatusForAllInvoices = (invoiceList) => {
    setIsShippingDataLoading(true);

    const getInvShippingStatus = async (currentInvoice) => {
      const uniqueShipmentIDs = new Map();
      const invoiceShippingStatus = {
        invoiceID: currentInvoice.invoiceID,
        shipments: new Map(),
        overallStatus: 'Not Selected'
      };
      // each lot has its own invoiceDetail
      currentInvoice.invoiceDetails.forEach((detail) => {
        if (detail.shippingShipmentID) {
          uniqueShipmentIDs.set(detail.shippingShipmentID, {
            shipmentID: detail.shippingShipmentID,
            memberRef: currentInvoice.memberRef
          });
        }
      });

      try {
        // fetch shipment data for each shipment
        if (uniqueShipmentIDs.size > 0) {
          const bulkShipmentDetails = await shippingApi.getBulkShipmentDetails(uniqueShipmentIDs, houseID);

          bulkShipmentDetails.data.shipments.forEach(shipment => {
            invoiceShippingStatus.shipments.set(shipment.id, {
              shippingMethod: shipment.option?.shippingMethod,
              artaShipmentID: shipment.booking?.artaShipmentID,
              shipmentStatus: shipment.booking?.artaShipmentStatus,
            });
          });
          if (bulkShipmentDetails.data.shipments.length === 1) {
            invoiceShippingStatus.overallStatus = bulkShipmentDetails.data.shipments[0].option?.shippingMethod;
          }
          if (bulkShipmentDetails.data.shipments.length > 1) {
            invoiceShippingStatus.overallStatus = `Multiple packages (${bulkShipmentDetails.data.shipments.length})`;
          }
        }
      } catch (err) {
        console.error(`Error fetching bulk shipment details for invoice ${currentInvoice.invoiceID}:`, err);
        invoiceShippingStatus.overallStatus = 'Error loading shipping data';
      }
      return invoiceShippingStatus;
    };

    const promises = invoiceList.map(async (invoice) => {
      const status = await getInvShippingStatus(invoice);
      return { invoiceID: invoice.invoiceID, status };
    });

    Promise.all(promises).then((results) => {
      const newShippingData = {};
      results.forEach(({ invoiceID, status }) => {
        newShippingData[invoiceID] = status;
      });
      setShippingData(newShippingData);
      setIsShippingDataLoading(false);
    });
  };

  useEffect(() => {
    if (!isInvShippingEnabled || !invoices || invoices.length === 0) {
      setShippingData({});
      return;
    }
    fetchShippingStatusForAllInvoices(invoices);
  }, [invoices, isInvShippingEnabled]);

  const getShippingData = (invoice) => {
    if (isShippingDataLoading) {
      return 'Loading...';
    } else if (invoice.invoiceDetails.some(detail => detail.shipped === true) ||
  (invoice.isShipped)) {
      return getInvoiceDetailStatus(invoice.invoiceID, 'ship');
    } else if (shippingData?.[invoice.invoiceID]?.overallStatus === 'Not Selected') {
      return 'Not Selected';
    }
    return shippingData?.[invoice.invoiceID];
  };

  const getLegacyPathForShipStatus = (currInvoice) => {
    if (!isInvShippingEnabled) return true;
    if (shippingData?.[currInvoice.invoiceID]?.overallStatus === 'Not Selected ') return true;
    // means it was set manually by the house admin
    if (currInvoice.invoiceDetails.some(detail => detail.shipped === true)) return true;
    if (currInvoice.isShipped) return true;
    return false;
  };
  //  formats additional data for table sorting and display
  useEffect(() => {
    if (filteredData.length > 0
      && (filteredData.length === eligibleStatus.length
        || filteredData.length > 0 && (searchTerm !== '' || searchFormCleared || viewFilterChange))) {
      const tableData = filteredData.map((invoice) => ({
        ...invoice,
        paidStatus: getInvoiceDetailStatus(invoice.invoiceID, 'paid').status,
        paidTimestamp: getInvoiceDetailStatus(invoice.invoiceID, 'paid').timestamp,
        shipStatus: isInvShippingEnabled ? getShippingData(invoice) : getInvoiceDetailStatus(invoice.invoiceID, 'ship'),
        useLegacyPathForShipStatus: getLegacyPathForShipStatus(invoice),
        balanceString: formatCurrencyForLocale(invoice.outStandingBalance),
        totalString: formatCurrencyForLocale(invoice.invoiceTotal),
        currencySymbol: invoice.totalPrice.currencySymbol,
        isPaid: nonRestrictedPaidStatus(invoice.invoiceID),
        isPending: isPendingPayment(invoice.invoiceID),
        paidByPaymentProcessing: nonRestrictedPaidByPP(invoice.invoiceID),
        paymentMethodText: getPaymentMethodText(invoice.invoiceDetails[0].paymentMethodID),
        paymentMethodID: invoice.invoiceDetails[0].paymentMethodID,
        isShipped: getInvoiceDetailStatus(invoice.invoiceID, 'ship').indexOf('Shipped') === 0
          || getInvoiceDetailStatus(invoice.invoiceID, 'ship').indexOf('Picked') === 0,
        hasRestrictedItems: getRestrictedStatus(invoice.invoiceID),
        approvalID: invoice.approvalID,
        hasNote: invoice.notes !== '',
        batchEligible: getEligibleStatus(invoice.invoiceID, 1),
        batchFailReason: getEligibleStatus(invoice.invoiceID, 0),
        autoChargeDate: (invoice.autoChargeDate === null) ? 0 : invoice.autoChargeDate.time,
        isPickup: get(invoice, 'pickup', false),
      }));

      setTableInvoiceData(tableData);
      setViewFilterChange(false);
    } else if (filteredData.length === 0) {
      setTableInvoiceData([]);
    }
  }, [filteredData, eligibleStatus, isInvShippingEnabled, shippingData, isShippingDataLoading]);

  useEffect(() => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === currentInvoiceID);
    if (currentInvoice) {
      setCurrentInvoiceNotes(currentInvoice.notes);
      const nameObject = {
        name: `${currentInvoice.lastName}, ${currentInvoice.firstName}`,
        paddleNumber: currentInvoice.paddleNumber,
        id: currentInvoice.invoiceID,
        location: `${currentInvoice.city}, ${currentInvoice.state}${currentInvoice.province} ${currentInvoice.country}`,
        channel: currentInvoice.channel,
      };
      setModalNameObject(nameObject);
    }
  }, [currentInvoiceID, isAddNoteModalOpen]);

  const exportFunctions = {
    handleOnAllChangeInvoicesChecked,
    isEveryInvoicesChecked,
    handleOnChangeInvoiceChecked,
    checkedInvoices,
    handleClickInvoiceLink,
    hasPaddles,
    setIsContactModalOpen,
    setIsAddNoteModalOpen,
    setCurrentInvoiceID,
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredData(invoices);
    setShowSearchError(false);
    setFilterView('all');
    setSearchFormCleared(true);
  };

  const handleSearch = (e) => {
    setShowSearchError(false);
    setSearchFormCleared(false);
    setSearchTerm(e.target.value);
    const displayedData = filterView === 'all' ? invoices : filteredData;
    const filteredArray = displayedData.filter(
      invoice => invoice.firstName.toLowerCase().includes(searchTerm.toLowerCase())
        || invoice.lastName.toLowerCase().includes(searchTerm.toLowerCase())
        || invoice.invoiceID.toString().includes(searchTerm.toString())
    );

    setFilteredData(filteredArray);
    if (filteredArray.length === 0) setShowSearchError(true);
  };

  const handleViewFilter = (e) => {
    let dataSet = invoices;
    const choice = e.currentTarget.value;
    if (choice === 'paid') {
      dataSet = invoices.filter(invoice => parseFloat(invoice.outStandingBalance) === 0);
    } else if (choice === 'unpaid') {
      dataSet = invoices.filter(invoice => parseFloat(invoice.outStandingBalance) > 0);
    }
    setFilterView(choice);
    setViewFilterChange(true);
    setFilteredData(dataSet);
  };

  useEffect(() => {
    setIsSingleRow(filteredData.length === 1);
  }, [filteredData]);

  const addUrlIDs = () => {
    let returnString = '';
    if (getSelectedIdArray().length) {
      returnString = `?id=${getSelectedIdArray().join(',')}`;
    } else if (filterView !== 'all') {
      const filteredIDs = filteredData.map(invoice => invoice.invoiceID);
      returnString = `?id=${filteredIDs.join(',')}`;
    }
    return returnString;
  };

  useEffect(() => {
    if (typeof groupInfoData === 'undefined') {
      return;
    }
    //  data object in charge modal
    let chargeableArray = invoices;
    const idArray = getSelectedIdArray();
    if (idArray.length > 0) {
      chargeableArray = invoices.filter(invoice => idArray.indexOf(invoice.invoiceID) > -1);
    } else {
      chargeableArray = invoices;
    }
    let invoiceCount = 0;
    const itemCount = 0;
    let eligibleCount = 0;
    let totalCharges = 0;
    const eligibleIdArray = [];
    const eligibleStatusArray = [];
    chargeableArray.forEach((invoice) => {
      if (!invoice.allPaid) {
        invoiceCount++;
      }
      if (!invoice.creditCardLimitExceeded && (invoice.batchChargeEligible || canProcessInvoice(invoice.invoiceID)) && !isPendingPayment(invoice.invoiceID)) {
        eligibleCount++;
        eligibleIdArray.push(invoice.invoiceID);
        // remove the commas
        const cleanAmount = invoice.outStandingBalance.toString().replaceAll(',', '');
        totalCharges += parseFloat(cleanAmount);
        eligibleStatusArray.push({
          eligible: 1,
          id: invoice.invoiceID,
          failReason: 'Ready to charge',
        });
      } else {
        let failReason = '';
        if (invoice.creditCardLimitExceeded) {
          failReason = 'Credit card limit exceeded';
        } else if (invoice.invoiceDetails.some((detail) => detail.additionalCharge && detail.additionalPriceInfo && detail.additionalPriceInfo.paid)) {
          failReason = 'Additional charges paid';
        } else if (invoice.additionalPriceInfo !== undefined && invoice.additionalPriceInfo !== null && invoice.additionalPriceInfo.paid) {
          failReason = 'Additional charges paid';
        } else if (!invoice.batchChargeEligible) {
          if (invoice.batchChargIneligibilityReason === 'INVOICE_PAID') {
            failReason = 'Invoice has been paid.';
          } else if (invoice.batchChargIneligibilityReason === 'INVOICE_NOT_SENT') {
            failReason = 'This invoice has not been sent to the bidder. 48 hours after the invoice is sent it will become eligible.';
          } else if (invoice.batchChargIneligibilityReason === 'CREDIT_CARD_LIMIT_EXCEEDED') {
            failReason = 'Credit card limit exceeded';
          } else if (invoice.batchChargIneligibilityReason === 'INVOICE_EDIT_NOT_SENT') {
            failReason = 'Edits were made to this invoice that have not been sent to the bidder. Please resend this invoice.';
          } else if (invoice.batchChargIneligibilityReason === 'INVOICE_SENT_LESS_THAN_2_DAYS') {
            failReason = 'This invoice was sent less than 48 hours ago.  It will become eligible 48 hours after it was last sent.';
          } else if (invoice.batchChargIneligibilityReason === 'INVOICE_PENDING_ACH') {
            failReason = 'This invoice has a pending ACH payment.';
          } else if (invoice.batchChargIneligibilityReason === 'AUTOCHARGE_SCHEDULED') {
            failReason = 'This invoice is scheduled to be auto-charged, and is not eligible for batch charge.';
          } else if (invoice.batchChargIneligibilityReason === 'HOUSE_PAYMENT_PROCESSING_DISABLED') {
            failReason = 'Enroll in Payment Processing to start using batch charging.';
          } else if (invoice.batchChargIneligibilityReason === 'NO_CREDIT_CARD_FOR_APPROVAL') {
            failReason = 'No Credit Card On File For This Bidder';
          } else if (invoice.batchChargIneligibilityReason === 'INVOICE_ITEMS_RESTRICTED') {
            failReason = 'Restricted items cannot be paid online, please collect payment offline.';
          } else if (invoice.batchChargIneligibilityReason === 'AVA_TAX_CALC_ERROR') {
            failReason = 'Tax failed to calculate for this invoice due to invalid bidder address. Bidder needs to update address on invoice.';
          } else if (invoice.batchChargIneligibilityReason === 'INVOICE_ITEMS_SUPPRESSED') {
            failReason = 'Remaining invoice items hidden';
          } else if (invoice.batchChargIneligibilityReason === 'FLOOR_BIDDER_INVOICE') {
            failReason = 'Floor bidder invoice, no credit card on file';
          } else {
            failReason = invoice.batchChargIneligibilityReason;
          }
        } else if (!canProcessInvoice(invoice.invoiceID)) {
          failReason = 'Cannot process invoice';
        } else if (isPendingPayment(invoice.invoiceID)) {
          failReason = 'Payment pending';
        } else if (invoice.allPaid) {
          failReason = 'Invoice already paid';
        }
        eligibleStatusArray.push({
          eligible: 0,
          id: invoice.invoiceID,
          failReason,
        });
      }
    });

    setEligibleStatus(eligibleStatusArray);

    const dataObject = {
      invoiceCount,
      itemCount,
      eligibleCount,
      totalCharges: `${currencySymbol}${totalCharges.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    };
    setEligibleInvoicesToProcess(eligibleIdArray);
    setChargeableInvoiceData(dataObject);
  }, [checkedInvoices, currencySymbol, groupInfoData, filteredData]);

  const updatePaidStatus = (id, isSuccess) => {
    const currentInvoiceTable = tableInvoiceData.find((invoice) => invoice.invoiceID === id);
    if (isSuccess) {
      currentInvoiceTable.paidStatus = `Paid - ${moment(new Date()).format('MM/DD/YY')}`;
      currentInvoiceTable.isPaid = true;
      currentInvoiceTable.paidByPaymentProcessing = true;
      currentInvoiceTable.balanceString = '0.00';
    } else {
      currentInvoiceTable.paidStatus = 'Payment Failed';
      currentInvoiceTable.isPaid = false;
    }
  };

  const processCreditCards = async () => {
    // manipulate invoicesToCharge w/quotes
    const invoicesQuoted = [];
    eligibleInvoicesToProcess.map((id) => {
      // check if over limit or already paid
      const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
      const limitExceeded = get(currentInvoice, 'creditCardLimitExceeded', false);
      const invoiceIsPayable = (!currentInvoice.allPaid
        || (currentInvoice.invoiceDetails.some((detail) => detail.additionalCharge && detail.additionalPriceInfo && !detail.additionalPriceInfo.paid))
        || (currentInvoice.additionalPriceInfo !== undefined && currentInvoice.additionalPriceInfo !== null && !currentInvoice.additionalPriceInfo.paid)
      );
      if (!limitExceeded && invoiceIsPayable && !isPendingPayment(id)) invoicesQuoted.push(id.toString());
    });
    setPaymentTotalCount(invoicesQuoted.length);

    try {
      setIsCardProcessing(true);
      chargeCards({
        data: invoicesQuoted,
      });
    } catch (err) {
      setIsChargeCardModalOpen(false);
      setIsCardProcessing(false);
      setShowPaymentErrorBanner(true);
      setShowPaymentErrorGeneric(true);
      console.log(err);
      return false;
    }

    setIsChargeCardModalOpen(false);
  };

  //  process the calls
  useEffect(() => {
    setIsCardProcessing(false);
    if (chargeCardsError) {
      setShowPaymentErrorBanner(true);
      setShowPaymentErrorGeneric(true);
    }

    if (typeof chargeCardsData === 'object' && chargeCardsData !== null) {
      const apiStatus = get(chargeCardsData, 'data.httpStatus', 0);
      const confirmationArray = get(chargeCardsData, 'data.data.invoiceConfirmations', []);
      const errorArray = [];
      let successCount = 0;
      let failureCount = 0;

      if (chargeCardsError || apiStatus !== 200) {
        setShowPaymentErrorBanner(true);
        setShowPaymentErrorGeneric(true);
      } else {
        let totalAmountCharged = 0;
        confirmationArray.forEach((invoice) => {
          if (invoice.charged) {
            successCount++;
            updatePaidStatus(invoice.invoiceID, true);
            totalAmountCharged += parseFloat(preformatFloat(invoice.amountCharged));
          } else {
            failureCount++;
            updatePaidStatus(invoice.invoiceID, false);
            errorArray.push(invoice.invoiceID);
          }
        });
        const newPending = parseFloat(preformatFloat(currentPendingInvoice)) - parseFloat(preformatFloat(totalAmountCharged));
        setCurrentPendingInvoice(newPending.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

        setPaymentSuccessCount(successCount);
        setPaymentErrorCount(failureCount);
        setPaymentErrorIdArray(errorArray);
        if (successCount) setShowPaymentSuccessBanner(true);
        if (failureCount) setShowPaymentErrorBanner(true);
      }
    }
  }, [chargeCardsData, chargeCardsError]);

  useEffect(() => {
    // filter to show failed payments
    if (paymentErrorIdArray.length) {
      setPreFilteredArray(tableInvoiceData);
      const filteredArray = tableInvoiceData.filter(
        invoice => paymentErrorIdArray.includes(invoice.invoiceID)
      );
      //  un-check success and ineligible
      Object.keys(checkedInvoices).forEach(id => {
        checkedInvoices[id] = paymentErrorIdArray.indexOf(Number(id)) > -1;
      });
      setTableInvoiceData(filteredArray);
    }
  }, [paymentErrorIdArray]);

  const clearPaymentErrorFilter = () => {
    setPaymentErrorIdArray([]);
    setTableInvoiceData(preFilteredArray);
    setShowPaymentSuccessBanner(false);
    setShowPaymentErrorBanner(false);
  };

  const openCalendar = (e) => {
    e.preventDefault();
    setCalendarID(Number(e.currentTarget.attributes.invoiceid.value));
    setCalendarDetailID(isInvShippingEnabled ? Number(e.currentTarget.attributes.detailid?.value) : -1);
    setCalendarVariant(e.currentTarget.attributes.variant.value);
    setCalendarPickupFlag(e.currentTarget.attributes.pickup.value === 'true');
    // Current Status is true if this invoice is paid or shipped (based on variant)
    setCalendarCurrentStatus(e.currentTarget.attributes.currentstatus.value === 'true');
    setIsCalendarModalOpen(true);
  };

  const handleSetShippedDate = (postData, variantText = '') => {
    try {
      execShippedDate({
        data: {
          paidInvoices: [postData],
        }
      });
      //update local data & screen
      const currentInvoice = tableInvoiceData.find(
        (invoice) => invoice.invoiceID === postData.invoiceID
      );
      const statusText = variantText === '' ? 'Shipped' : variantText;
      if (postData.shipped) {
        currentInvoice.shipStatus = `${statusText} - ${postData.shippedDate}`;
        currentInvoice.isShipped = true;
      } else {
        currentInvoice.shipStatus = `Not ${statusText}`;
        currentInvoice.isShipped = false;
      }
      setTableInvoiceData(() => tableInvoiceData.map(invoice => (invoice.invoiceID === postData.invoiceID ? currentInvoice : invoice)));
    } catch (err) {
      console.error(err);
    }

    setIsCalendarModalOpen(false);
  };

  const handleSetPaidDate = (postData) => {
    try {
      execPaidDate({
        data: {
          paidInvoices: [postData],
        }
      });

      //update local data & screen
      const currentInvoice = tableInvoiceData.find(
        (invoice) => invoice.invoiceID === postData.invoiceID
      );
      if (postData.paid) {
        currentInvoice.paidStatus = `Paid - ${postData.paidDate}`;
        currentInvoice.isPaid = true;
      } else {
        currentInvoice.paidStatus = 'Sent';
        currentInvoice.isPaid = false;
      }
    } catch (err) {
      console.error(err);
    }

    setIsCalendarModalOpen(false);
  };

  const getPaymentTextForExport = (id, text) => {
    let returnString = '';
    if (text.trim() === 'paidByWePay') {
      returnString = 'Paid by payment processing';
    } else {
      returnString = getPaymentMethodText(id);
    }

    return returnString;
  };

  const exportAsFile = () => {
    const headers = 'Invoice #,Paddle,Name,Platform,Address,Invoice Total,Outstanding Bal.,Invoice Status,Payment Method,Payment Date,Ship Status,Ship Date';
    const fileName = `invoiceGroup_${invoiceGroupID}`;
    const fileData = [];
    const downlodData = filterView !== 'all' ? filteredData : invoices;
    downlodData.forEach((invoice) => {
      const fullName = `${invoice.lastName}, ${invoice.firstName}`;
      let fullAddress = invoice.city;
      if (invoice.state) fullAddress += `, ${invoice.state}`;
      fullAddress += `, ${invoice.country}`;
      const paidStatus = getInvoiceDetailStatus(invoice.invoiceID, 'paid', true);
      const shipStatus = getInvoiceDetailStatus(invoice.invoiceID, 'ship', true);
      const invoiceRow = [
        invoice.invoiceID,
        invoice.paddleNumber,
        `"${fullName}"`,
        formatChannelText(invoice.channel),
        `"${fullAddress}"`,
        `"${invoice.totalPrice.currencySymbol} ${invoice.invoiceTotal}"`,
        `"${invoice.totalPrice.currencySymbol} ${invoice.outStandingBalance}"`,
        paidStatus[0],
        getPaymentTextForExport(invoice.invoiceDetails[0].paymentMethodID, invoice.invoiceDetails[0].paymentMethod),
        paidStatus[1],
        shipStatus[0],
        shipStatus[1]
      ];
      fileData.push(invoiceRow);
    });
    exportCSV(headers, fileData, fileName);
  };

  const sendToAutoCharge = () => {
    let idArray = getSelectedIdArray();
    if (idArray.length === 0) {
      idArray = allInvoicesIds;
    }
    const idList = idArray.join(',');
    window.location = `${imagePath}/invoices/InvoiceAutoCharge/${invoiceGroupID}/invoice/${idList}`;
  };

  if (isIdle || isLoading || isCardProcessing) {
    return (
      <ReactLoading className="loading-spinner" type="spin" color="black" />
    );
  }

  if (error !== null || !invoices.length) {
    return (
      <>
        <Page
          className={blockEl}
          header={{ title: 'Invoice Group Details' }}
          navLink={{
            displayName: 'Back to Existing Invoices',
            href: '/invoices/ExistingInvoices',
          }}
        >
          <div>
            {error !== null && (
              <span>There was an error processing your request.  Please try again later.</span>
            )}
            {(error === null && !invoices.length) && (
              <span>No invoices have been generated.</span>
            )}
          </div>
        </Page>
      </>
    );
  }

  return (
    <Fragment>
      {isInvShippingEnabled && <Container><InvaluableShippingBanner /></Container>}
      {paymentProcessingEnabled && (processorType === 'p') && (
        <Container>
          <Row>
            <Col xs={12} className="px-0 mt-3">
              <div className="alert alert-info mt-2 mb-2" role="alert">
                <div className="alert-content">
                  <span className="float-left">
                    <FontAwesomeIcon icon="question-circle" />
                  </span>
                  <div className="pl-2 ml-4 ">
                    New Feature For Invaluable Payments - Add Additional Charges To Paid Invoices.
                    Example:  Get paid for the hammer & BP first and add shipping charges later.
                    <a href="https://www.invaluableauctions.com/inv/help-center/resource/invoicing/add-additional-charges-to-a-paid-invoice/" target="_blank" rel="noopener noreferrer" className="ml-2 text-decoration-underline">Learn more</a>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      )}
      <Page
        className={blockEl}
        header={{ title: 'Invoice Group Details' }}
        navLink={{
          displayName: 'Back to Existing Invoices',
          href: '/invoices/ExistingInvoices',
        }}
      >
        <Container>
          <Row className={`${blockEl}__header`}>
            <Col className={`${blockEl}__sessions-in-group`} xs={5}>
              <Table borderless>
                <thead>
                  <tr>
                    <th>Sessions in Group</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSessionsInGroup.length
                    ? sortedSessionsInGroup.map((sessionGroup) => (
                      <tr key={sessionGroup.catalogID}>
                        <td>
                          {sessionGroup.identifier}: {sessionGroup.title} -
                          Day {getDiffDateNumber(sessionGroup.date)}
                        </td>
                        <td className="ml-3">
                          {sessionGroup.date}
                        </td>
                      </tr>
                    ))
                    : null}
                </tbody>
              </Table>
            </Col>
            <Col className={`${blockEl}__totals`}>
              <Table borderless>
                <thead>
                  <tr>
                    <th>Total Invoices</th>
                    <th>Total Items</th>
                    <th>Invoice Total</th>
                    <th>Total Pending Charges</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{filterView === 'all' ? invoices.length : filteredData.length}</td>
                    <td>{totalItemCount}</td>
                    <td>{currencySymbol}{currentTotalInvoice}</td>
                    <td>{currencySymbol}{currentPendingInvoice}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>

          {showPaymentSuccessBanner && (
            <div className="alert alert-success">
              <div className="d-inline pt-3">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="alert-content d-inline ml-3">
                {paymentSuccessCount}/{paymentTotalCount} of your payments have been processed!.
              </div>
            </div>
          )}

          {showPaymentErrorBanner && (
            <div className="alert alert-danger">
              <div className="d-inline-block">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="alert-content d-inline-block ml-3">
                {showPaymentErrorGeneric ? (
                  <div>
                    An error occurred while processing your request.  No cards were charged.  Please try again.
                  </div>
                ) : (
                  <>
                    <div className="font-weight-bold">
                      {paymentErrorCount}/{paymentTotalCount} of your payments failed.
                    </div>
                    <div>
                      Some payments were not processed due to invalid payment type.  Please contact the bidder to arrange payment.
                      <button
                        className="btn btn-link btn-inline"
                        onClick={() => clearPaymentErrorFilter()}
                        type="button"
                      >
                        Clear filter
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <Row>
            <Col xs="12">
              <Alert
                color="warning"
                className={`${blockEl}__ach-banner`}
                isOpen={showAchBanner}
                toggle={() => {
                  setShowAchBanner(false);
                }}
              >
                <span>
                  <FontAwesomeIcon icon={faInfo} />
                </span>
                Based on your set credit card limit, {invoicesWithACHPaymentRequiredCount}/{invoices.length} invoices will require ACH payment and cannot be automatically charged.
              </Alert>
            </Col>
          </Row>
          <Row className={`${blockEl}__table-actions mb-1`}>
            <Col xs={8} className={`${blockEl}__button-actions`}>
              <div>
                <Button
                  color="link"
                  aria-label="print"
                  onClick={() => setPrintModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faPrint} />
                  {(getSelectedIdArray().length === 0) ? (
                    <span> Print all</span>
                  ) : (
                    <span> Print selected</span>
                  )}
                </Button>
                <Button color="link" onClick={handleOnEditInvoices}>
                  <FontAwesomeIcon icon={faPenSquare} />
                  <Link to={`/invoices/EditInvoices/${invoiceGroupID}${addUrlIDs()}`}>
                    Edit {getSelectedIdArray().length === 0 && ('all')} invoices
                  </Link>
                </Button>
                {showChargeCardLink && !showPaymentErrorBanner && !showPaymentSuccessBanner && (
                  <Button color="link" onClick={() => setIsChargeCardModalOpen(true)}>
                    <FontAwesomeIcon icon={faCreditCard} />
                    Charge {getSelectedIdArray().length === 0 ? ('all') : ('selected')} cards
                  </Button>
                )}
                <Button color="link" onClick={exportAsFile}>
                  <FontAwesomeIcon icon={faDownload} />
                  Download as CSV
                </Button>

                {autoChargeEnabled && (
                  <Button color="link" onClick={() => sendToAutoCharge()}>
                    <FontAwesomeIcon icon={faDollarSign} />
                    Manage AutoCharge
                  </Button>
                )}

                {getSelectedIdArray().length > 0 && (
                  <>
                    <Button color="link" onClick={() => setIsBulkContactModalOpen(true)}>
                      <FontAwesomeIcon icon={faEnvelope} />
                      Bulk contact
                    </Button>
                    <Button color="link" onClick={() => setIsBulkAddNoteModalOpen(true)}>
                      <FontAwesomeIcon icon={faStickyNote} />
                      Bulk add note
                    </Button>
                  </>
                )}
              </div>
              <div className="w-115">
                <div className="float-left">
                  <ToggleButtonGroup
                    value={filterView}
                    exclusive
                    onChange={e => handleViewFilter(e)}
                    aria-label="view filter"
                    color="primary"
                  >
                    <ToggleButton value="all" aria-label="all invoices" className="mr-0">
                      ALL
                    </ToggleButton>
                    <ToggleButton value="unpaid" aria-label="unpaid invoices" className="mr-0">
                      UNPAID
                    </ToggleButton>
                    <ToggleButton value="paid" aria-label="paid invoices">
                      PAID
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
                {getSelectedIdArray().length > 0 && (
                  <div className="float-left ml-4 mt-4">
                    {`${getSelectedIdArray().length} ${getSelectedIdArray().length > 1 ? 'invoices' : 'invoice'} selected`}
                  </div>
                )}
                <div className="clearfix" />
              </div>
            </Col>
            <Col xs={4} className="text-right">
              <div>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={disablePreviewButton}
                  onClick={handlePreviewButton}
                >
                  Preview and Send Selected
                </button>
              </div>
              <div className="mt-3">
                <InputGroup className="customers-search">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <FontAwesomeIcon icon="search" className="small" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="search"
                    name="search"
                    id="invoiceSearch"
                    className="cancel-icon-search small-important"
                    placeholder="Search by name or invoice number"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyUp={e => {
                      if ((e.key === 'Delete' || e.key === 'Backspace') && searchTerm.length === 0) {
                        clearSearch();
                      } else {
                        handleSearch(e);
                      }
                    }}
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText className="cancel-button">
                      <button
                        className="cancel-icon"
                        type="button"
                        aria-label="Cancel"
                        onClick={() => clearSearch()}
                      >
                        <FontAwesomeIcon icon={faCircleXmark} className="color-gray-bg small" />
                      </button>
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </div>
              <div className="text-center password-error">
                {showSearchError && (
                  'There are no results.  Please enter a different search and try again.'
                )}
              </div>
            </Col>
          </Row>
          <Row className={getSelectedIdArray().length > 0 ? 'mt-5 pt-2' : 'mt-2'}>
            <Col xs={12} className="mt-2">
              <ReactTable
                className="-highlight mt-4 w-115 rt-table-invoice"
                minRows={1}
                data={tableInvoiceData}
                defaultPageSize={50}
                pageSizeOptions={[50, 100, 200]}
                columns={InvoiceGroupTableCols(
                  invoiceGroupID,
                  exportFunctions,
                  openCalendar,
                  isSingleRow,
                  paymentProcessingEnabled,
                  setMoreMenuId,
                  moreMenuId,
                  moreMenuRef
                )}
                rowsText="per page"
                noDataText="No invoices have been generated"
                showPageJump={false}
                handleOnAllChangeInvoicesChecked={handleOnAllChangeInvoicesChecked}
                isEveryInvoicesChecked={isEveryInvoicesChecked}
                setIsAddNoteModalOpen={setIsAddNoteModalOpen}
                setIsContactModalOpen={setIsContactModalOpen}
                setCurrentInvoiceID={setCurrentInvoiceID}
              />
            </Col>

            <Col xs={12} className="mt-2 mb-5">
              {showRestrictedItemText && paymentProcessingEnabled && (
                <div>
                  * these invoices contain one or more restricted items that cannot be paid for online.
                </div>
              )}
            </Col>

          </Row>
        </Container>
      </Page>

      <PrintableInvoiceModal
        isOpen={printModalOpen}
        printableInvoiceData={printableInvoiceData}
        printableInvoiceIds={printableInvoiceIds}
        setPrintModalOpen={setPrintModalOpen}
        ref={printRef}
      />

      <AddNoteModal
        isOpen={isBulkAddNoteModalOpen}
        variant="bulk"
        totalInvoices={getSelectedIdArray().length}
        onClose={() => {
          setIsBulkAddNoteModalOpen(false);
        }}
        onSave={handleOnBulkAddNote}
      />
      <ContactModal
        isOpen={isBulkContactModalOpen}
        variant="bulk"
        totalInvoices={getSelectedIdArray().length}
        onClose={() => {
          setIsBulkContactModalOpen(false);
        }}
        onSave={handleOnBulkContact}
      />
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        modalNameObject={modalNameObject}
        variant="single"
        notes={currentInvoiceNotes}
        onClose={() => {
          setIsAddNoteModalOpen(false);
        }}
        onSave={() => handleInlineNote()}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        modalNameObject={modalNameObject}
        variant="single"
        onClose={() => {
          setIsContactModalOpen(false);
        }}
        onSave={handleInlineContact}
      />
      <ChargeCardModal
        isOpen={isChargeCardModalOpen}
        onClose={() => {
          setIsChargeCardModalOpen(false);
        }}
        invoiceIdArray={getSelectedIdArray()}
        chargeableInvoiceData={chargeableInvoiceData}
        currencySymbol={currencySymbol}
        pendingChargesData={currentPendingInvoice}
        onSave={processCreditCards}
      />
      <CalendarModal
        isOpen={isCalendarModalOpen}
        invoiceID={calendarID}
        invoiceDetailID={calendarDetailID}
        variant={calendarVariant}
        pickup={calendarPickupFlag}
        paymentMethods={paymentMethods}
        paymentMethodID={calendarPaymentMethod}
        presetDate={calendarDate}
        onClose={() => {
          setIsCalendarModalOpen(false);
        }}
        onSave={(calendarVariant === 'Shipped') ? handleSetShippedDate : handleSetPaidDate}
        currentStatus={calendarCurrentStatus}
      />

    </Fragment>
  );
};
export default InvoiceGroupDetails;
